import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Smartphone, Settings, Moon, Sun, HelpCircle, Zap, Loader2, Wrench, Wifi } from 'lucide-react'
import { useDeviceStore } from '@/stores/device.store'
import { useLogStore } from '@/stores/log.store'
import { webAdbService } from '@/services/webadb.service'
import { HiTrackDialog } from '@/components/HiTrackDialog'
import { HelpDialog } from '@/components/HelpDialog'

// 下载文本文件
const downloadTextFile = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

type WifiInfo = Awaited<ReturnType<typeof webAdbService.getWifiInfo>>

export function Header() {
  const { isConnected, isConnecting, deviceName, connect, disconnect, adb } = useDeviceStore()
  const { addLog } = useLogStore()
  const [hiTrackOpen, setHiTrackOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))
  const [wifiInfo, setWifiInfo] = useState<WifiInfo | null>(null)
  const [wifiLoading, setWifiLoading] = useState(false)
  const [bugreportOpen, setBugreportOpen] = useState(false)
  const [bugreportProgress, setBugreportProgress] = useState({ status: '', percent: 0 })

  const loadWifiInfo = useCallback(async () => {
    if (!adb) return
    setWifiLoading(true)
    try {
      const info = await webAdbService.getWifiInfo(adb)
      setWifiInfo(info)
    } finally {
      setWifiLoading(false)
    }
  }, [adb])

  useEffect(() => {
    if (isConnected && adb) {
      loadWifiInfo()
    } else {
      setWifiInfo(null)
    }
  }, [isConnected, adb, loadWifiInfo])

  const toggleTheme = () => {
    const newDark = !isDark
    setIsDark(newDark)
    document.documentElement.classList.toggle('dark', newDark)
    localStorage.setItem('theme', newDark ? 'dark' : 'light')
  }

  useEffect(() => {
    const saved = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const shouldBeDark = saved ? saved === 'dark' : prefersDark
    setIsDark(shouldBeDark)
    document.documentElement.classList.toggle('dark', shouldBeDark)
  }, [])

  return (
    <header className="h-14 border-b flex items-center justify-between px-4 bg-card">
      {/* Logo + Device Status */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-lg">数据提取整理工具 </span>
        </div>

        {/* Device Status */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" disabled={isConnecting}>
              {isConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Smartphone className="w-4 h-4 text-muted-foreground" />
              )}
              {isConnected ? (
                <>
                  <span className="max-w-[200px] truncate">{deviceName}</span>
                  <Badge variant="default" className="bg-green-600 hover:bg-green-600 h-5 px-1.5 text-[10px]">已连接</Badge>
                </>
              ) : (
                <span className="text-muted-foreground">未连接设备</span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {isConnected ? (
              <DropdownMenuItem onClick={() => disconnect()} className="text-destructive">断开连接</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => connect()}>连接新设备</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Wrench className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setHiTrackOpen(true)} disabled={!isConnected}>
              华为 HiTrack 运动数据提取
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!isConnected}
              onClick={async () => {
                if (!adb) return
                try {
                  const content = await webAdbService.exportLogcat(adb)
                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
                  downloadTextFile(content, `logcat_${timestamp}.txt`)
                } catch (e: any) {
                  addLog('error', `导出 Logcat 失败: ${e.message}`)
                }
              }}
            >
              导出 Logcat 日志
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!isConnected}
              onClick={async () => {
                if (!adb) return
                try {
                  await webAdbService.clearLogcat(adb)
                } catch (e: any) {
                  addLog('error', `清除 Logcat 失败: ${e.message}`)
                }
              }}
            >
              清除 Logcat 日志
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!isConnected}
              onClick={async () => {
                if (!adb) return
                setBugreportProgress({ status: '正在启动...', percent: 0 })
                setBugreportOpen(true)
                try {
                  const content = await webAdbService.generateBugreport(adb, (status, percent) => {
                    setBugreportProgress({ status, percent })
                  })
                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
                  downloadTextFile(content, `bugreport_${timestamp}.txt`)
                  setTimeout(() => setBugreportOpen(false), 1000)
                } catch (e: any) {
                  addLog('error', `生成 Bugreport 失败: ${e.message}`)
                  setBugreportOpen(false)
                }
              }}
            >
              生成 Bugreport
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {wifiInfo?.connected && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" title="Wi-Fi 信息">
                {wifiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2">
                <Wifi className="w-4 h-4" />
                {wifiInfo.ssid}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {wifiInfo.frequency && <DropdownMenuItem disabled>频率: {wifiInfo.frequency}</DropdownMenuItem>}
              {wifiInfo.wifiVersion && <DropdownMenuItem disabled>版本: {wifiInfo.wifiVersion}</DropdownMenuItem>}
              {wifiInfo.macAddress && <DropdownMenuItem disabled>MAC: {wifiInfo.macAddress}</DropdownMenuItem>}
              {wifiInfo.ipAddresses.map((ip, i) => (
                <DropdownMenuItem key={i} disabled>IP: {ip}</DropdownMenuItem>
              ))}
              {(wifiInfo.txLinkSpeed || wifiInfo.linkSpeed) && (
                <DropdownMenuItem disabled>上传: {wifiInfo.txLinkSpeed || wifiInfo.linkSpeed}</DropdownMenuItem>
              )}
              {(wifiInfo.rxLinkSpeed || wifiInfo.linkSpeed) && (
                <DropdownMenuItem disabled>下载: {wifiInfo.rxLinkSpeed || wifiInfo.linkSpeed}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button variant="ghost" size="icon">
          <Settings className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleTheme} title={isDark ? '切换到亮色模式' : '切换到暗色模式'}>
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
        <Button variant="ghost" size="icon" onClick={() => setHelpOpen(true)} title="连接帮助">
          <HelpCircle className="w-4 h-4" />
        </Button>
      </div>

      <HiTrackDialog open={hiTrackOpen} onOpenChange={setHiTrackOpen} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />

      {/* Bugreport 进度对话框 */}
      <Dialog open={bugreportOpen} onOpenChange={setBugreportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>生成 Bugreport</DialogTitle>
            <DialogDescription>
              正在收集设备诊断信息，请稍候...
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{bugreportProgress.status || '准备中...'}</span>
                <span className="font-medium">{bugreportProgress.percent}%</span>
              </div>
              <Progress value={bugreportProgress.percent} className="h-2" />
            </div>
            <p className="text-xs text-muted-foreground">
              提示：某些设备需要切换到"文件传输"模式才能正常生成
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  )
}
